import { Endpoint } from "shared/utils/endpoints";
import { SCHOOL_HTTP_CLIENT } from "shared/utils/schoolInterceptor";
import { getSchoolScopeId, scoped, setSchoolScopeId } from "./schoolScope";

/**
 * School Head Office analytics APIs, called from the admin panel.
 *
 * These endpoints live on the school API (`/api/school/head-office/*`) and are
 * written for a logged-in head office, so every call here is scoped to a school
 * with `school_id`. The admin panel sets that once per screen through
 * `setHeadOfficeSchoolId`, which keeps the ported dashboard components
 * unchanged — they call these functions exactly as the school app does.
 *
 * Common params:
 *  - `from`, `to`      — optional reporting window (YYYY-MM-DD).
 *  - `per_page`,`page` — paginated listings only (per_page 1-50).
 *  - `types`           — reading-mix dimensions (genre|language|age_range|keyword).
 *  - `metrics`         — reading-growth-trend metrics.
 */

const HO = Endpoint.school_head_office;

/**
 * `setHeadOfficeSchoolId` is kept as the head-office-shaped name for the shared
 * scope so callers read naturally; both analytics services share one scope.
 */
export const setHeadOfficeSchoolId = setSchoolScopeId;
export const getHeadOfficeSchoolId = getSchoolScopeId;

interface DateRangeParams {
  from?: string;
  to?: string;
}

interface PaginatedParams extends DateRangeParams {
  per_page?: number;
  page?: number;
}

/** Campus drill-down lens: which slice of the campus to return. */
interface CampusDrilldownParams extends PaginatedParams {
  /** class (default) | student | teacher */
  type?: "class" | "student" | "teacher";
}

/** Head-office KPI tiles (campuses, students, classrooms, books, pages read). */
const getDashboard = (params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.dashboard, { params: scoped(params) });

/** Per-campus reading summary, sorted by total_books_read desc (paginated). */
const getReaderCampus = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.readerCampus, { params: scoped(params) });

/** One campus's all-time reading detail. `campusId` is the campus user_id. */
const getReaderCampusDetail = (campusId: number | string) =>
  SCHOOL_HTTP_CLIENT.get(HO.readerCampusDetail + campusId, {
    params: scoped(),
  });

/**
 * Campus drill-down through a single lens (paginated).
 * `type=class|student|teacher`; `campusId` is the campus user_id.
 */
const getCampusDrilldown = (
  campusId: number | string,
  params?: CampusDrilldownParams
) =>
  SCHOOL_HTTP_CLIENT.get(HO.campusDrilldown + campusId, {
    params: scoped(params),
  });

/** One student's reading record, honouring the optional window. */
const getStudentDetail = (
  studentId: number | string,
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(HO.studentDetail + studentId, {
    params: scoped(params),
  });

/** One teacher's reading record, honouring the optional window. */
const getTeacherDetail = (
  teacherId: number | string,
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(HO.teacherDetail + teacherId, {
    params: scoped(params),
  });

/** Teachers across all campuses ranked by completed then assigned books. */
const getTopTeachers = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.topTeachers, { params: scoped(params) });

/** Students across all campuses ranked by completed books then badges. */
const getTopStudents = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.topStudents, { params: scoped(params) });

/** Whole-school adoption rate + age-appropriate share. */
const getAdoptionRate = (params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.adoptionRate, { params: scoped(params) });

/**
 * Share-of-reads per dimension.
 * @param types one or more of genre|language|age_range|keyword (comma-separated).
 */
const getReadingMix = (types: string = "genre", params?: DateRangeParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.readingMix, { params: scoped({ types, ...params }) });

/** Headline content stats + per-campus completed-book counts (paginated). */
const getBookContentAnalysis = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.bookContentAnalysis, { params: scoped(params) });

/** Campus-by-campus comparison on adoption / quiz / completion (paginated). */
const getCampusComparison = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.campusComparison, { params: scoped(params) });

/** Student-slot capacity of the school. */
const getCapacity = () =>
  SCHOOL_HTTP_CLIENT.get(HO.capacity, { params: scoped() });

/** Per-campus teacher engagement (paginated). */
const getTeacherAdoption = (params?: PaginatedParams) =>
  SCHOOL_HTTP_CLIENT.get(HO.teacherAdoption, { params: scoped(params) });

/**
 * Reading-growth line-graph series over the window (window <= 365 days).
 * @param metrics one or more of books_read|pages_read|cumulative_reading|
 *                learning_rate|book_completion_rate|avg_books_per_active_student.
 */
const getReadingGrowthTrend = (
  metrics: string = "books_read",
  params?: DateRangeParams
) =>
  SCHOOL_HTTP_CLIENT.get(HO.readingGrowthTrend, {
    params: scoped({ metrics, ...params }),
  });

export {
  getDashboard,
  getReaderCampus,
  getReaderCampusDetail,
  getCampusDrilldown,
  getStudentDetail,
  getTeacherDetail,
  getTopTeachers,
  getTopStudents,
  getAdoptionRate,
  getReadingMix,
  getBookContentAnalysis,
  getCampusComparison,
  getCapacity,
  getTeacherAdoption,
  getReadingGrowthTrend,
};
